import { useEffect, useState } from "react";
import { toast } from "sonner";
import { inputClass } from "@/components/Shared";
import { getPricing, putPricing } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

const SECTION_LABELS = {
  consultations: "Consultations",
  regenerative: "Rejuran and regenerative",
  threads: "Threads",
  skin: "Skin and laser",
  minor: "Minor procedures",
  body: "Body and core-to-floor",
};

const AdminPricing = ({ token, onUnauthorized }) => {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPricing().then(setPricing).catch(() => toast.error("Couldn't load pricing."));
  }, []);

  const updateRow = (section, i, field, value) => {
    setPricing((p) => {
      const rows = p[section].rows.map((r, ri) => (ri === i ? { ...r, [field]: value } : r));
      return { ...p, [section]: { ...p[section], rows } };
    });
  };

  const updatePrice = (section, i, pi, value) => {
    setPricing((p) => {
      const rows = p[section].rows.map((r, ri) =>
        ri === i
          ? { ...r,
              prices: r.prices.map((pr, pri) => (pri === pi ? value : pr)),
              ...(value.trim() && value.trim() !== "—" ? { tbc: false } : {}) }
          : r
      );
      return { ...p, [section]: { ...p[section], rows } };
    });
  };

  const addRow = (section) => {
    setPricing((p) => ({
      ...p,
      [section]: {
        ...p[section],
        rows: [...p[section].rows, { name: "", sub: "", prices: p[section].columns.map(() => "") }],
      },
    }));
  };

  const removeRow = (section, i) => {
    setPricing((p) => ({
      ...p,
      [section]: { ...p[section], rows: p[section].rows.filter((_, ri) => ri !== i) },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await putPricing(token, pricing);
      toast.success("Pricing saved — the website is updated.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!pricing) return <p className="text-soft-grey text-sm py-16 text-center">Loading…</p>;

  return (
    <div data-testid="pricing-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-ink/60 text-sm max-w-xl">
          Edit names, notes and prices below, then save once. Changes appear on the pricing page immediately.
          Never add prices for injectable or other prescription treatments.
        </p>
        <button onClick={save} disabled={saving} data-testid="pricing-save-button"
          className="btn-gold px-7 py-3 text-sm font-medium tracking-[0.06em] disabled:opacity-60">
          {saving ? "Saving…" : "Save pricing"}
        </button>
      </div>

      <div className="mt-8 space-y-10">
        {Object.keys(SECTION_LABELS).filter((s) => pricing[s]).map((section) => {
          const data = pricing[section];
          return (
            <div key={section} className="bg-cream relative p-7" data-testid={`pricing-section-${section}`}>
              <h2 className="font-serif font-semibold text-deep-sea text-xl mb-5">{SECTION_LABELS[section]}</h2>
              <div className="space-y-3">
                <div className="hidden md:flex gap-3 text-soft-grey text-[11px] tracking-[0.14em] uppercase font-medium">
                  <span className="flex-[2]">Treatment</span><span className="flex-[1.4]">Note</span>
                  {data.columns.map((c) => <span key={c} className="flex-1">{c}</span>)}
                  <span className="w-8" />
                </div>
                {data.rows.map((row, i) => (
                  <div key={i} className="flex flex-col md:flex-row gap-3 md:items-center" data-testid={`pricing-row-${section}-${i}`}>
                    <input value={row.name} onChange={(e) => updateRow(section, i, "name", e.target.value)}
                      placeholder="Treatment name" data-testid={`pricing-name-${section}-${i}`} className={`${inputClass} md:flex-[2]`} />
                    <input value={row.sub || ""} onChange={(e) => updateRow(section, i, "sub", e.target.value)}
                      placeholder="Note (e.g. 60 min)" data-testid={`pricing-sub-${section}-${i}`} className={`${inputClass} md:flex-[1.4]`} />
                    {row.prices.map((price, pi) => (
                      <input key={pi} value={price} onChange={(e) => updatePrice(section, i, pi, e.target.value)}
                        placeholder={data.columns[pi]} data-testid={`pricing-price-${section}-${i}-${pi}`} className={`${inputClass} md:flex-1`} />
                    ))}
                    <button onClick={() => removeRow(section, i)} data-testid={`pricing-delete-${section}-${i}`}
                      className="text-soft-grey hover:text-red-700 transition-colors p-2 self-end md:self-auto" aria-label="Remove row">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={() => addRow(section)} data-testid={`pricing-add-${section}`}
                className="mt-4 inline-flex items-center gap-2 text-sea-green hover:text-gold text-[11px] tracking-[0.14em] uppercase font-medium transition-colors">
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add a row
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminPricing;
